import { useCallback, useEffect, useState } from 'react';
import { getLoginSessions, revokeLoginSession, type LoginSession } from '../../../api/user';
import { useAuthStore } from '../../../stores/authStore';
import { handleApiError, showErrorNotification } from '../../../utils/errorHandler';
import { toLocalDateString } from '../../../utils/dateUtils';

/** UTC 瞬间 → 本地「年月日 时分」；裸日期不能走这条路，所以只用于带时区的 ISO 串 */
function formatInstant(iso: string | null): string {
  if (!iso) return '-';
  const d = new Date(iso);
  return isNaN(d.getTime()) ? '-' : d.toLocaleString('zh-CN');
}

/**
 * 登录设备管理：列出本账号当前在用的会话，可逐台下线。
 *
 * 设备名与「是否本机」都由后端算好（`deviceLabel` / `current`）——User-Agent 的解析口径
 * 一旦散到四端各写一份，同一台手机在 web 上看是「安卓设备」、在鸿蒙上看是「未知设备」，
 * 用户就没法据此判断哪台不是自己。
 * 取数失败整块换成「重新获取」而不是空列表：设备清单是安全信息，
 * 空列表等于告诉用户「只有本机在用」，那是最坏的一种假象。
 */
export default function LoginDevicesPanel() {
  const { signOut } = useAuthStore();
  const [sessions, setSessions] = useState<LoginSession[] | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState('');
  // 确认状态放在组件里而不是 window.confirm：要让用户看得见下线的是哪一台
  const [confirmId, setConfirmId] = useState<string | null>(null);
  const [revokingId, setRevokingId] = useState<string | null>(null);
  const [revokeError, setRevokeError] = useState('');
  // 下线成功的提示不能只靠「那一行消失了」：列表一长，被删的行可能在视口外
  const [hint, setHint] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    setLoadError('');
    setHint('');
    try {
      setSessions(await getLoginSessions());
    } catch (error) {
      setSessions(null);
      setLoadError(handleApiError(error).message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const handleRevoke = async (session: LoginSession) => {
    setConfirmId(null);
    setRevokeError('');
    setRevokingId(session.id);
    try {
      const res = await revokeLoginSession(session.id);
      if (res?.self) {
        // 本机的刷新会话已被后端吊销并清了 cookie，令牌留在手里只会让下一次请求
        // 变成看不懂的「登录已过期」，这里直接走正常登出并回登录页。
        await signOut();
        window.location.href = '/login';
        return;
      }
      await load();
      setHint(`${session.deviceLabel} 已下线，该设备需重新登录`);
    } catch (error) {
      const info = handleApiError(error);
      setRevokeError(info.message);
      showErrorNotification(info);
    } finally {
      setRevokingId(null);
    }
  };

  return (
    <div className="w-full sm:max-w-md space-y-3">
      <div className="flex items-start justify-between gap-3">
        <div>
          <p className="text-sm font-semibold text-gray-900">登录设备管理</p>
          <p className="text-xs text-gray-500 mt-0.5 leading-relaxed">
            下面这些设备正在用你的账号保持登录。发现不认识的设备，先下线它，再修改密码。
          </p>
        </div>
        <button
          type="button"
          onClick={load}
          disabled={loading || revokingId !== null}
          className="shrink-0 flex items-center gap-1.5 px-3 py-1.5 text-sm text-gray-600 border border-gray-200 rounded-lg hover:bg-gray-50 disabled:opacity-50 transition-colors cursor-pointer whitespace-nowrap"
        >
          <i className={loading ? 'ri-loader-4-line animate-spin' : 'ri-refresh-line'} />
          {loading ? '加载中' : '刷新'}
        </button>
      </div>

      {!loading && loadError && (
        <div className="flex items-center justify-between gap-3 p-3 bg-gray-50 border border-red-100 rounded-lg">
          <span className="text-sm text-red-600 flex items-center gap-2">
            <i className="ri-error-warning-line" />
            {loadError}
          </span>
          <button
            type="button"
            onClick={load}
            className="shrink-0 px-3 py-1.5 text-xs font-medium text-gray-600 border border-gray-200 rounded-lg hover:bg-white cursor-pointer whitespace-nowrap"
          >
            重新获取
          </button>
        </div>
      )}

      {!loading && !loadError && sessions && sessions.length === 0 && (
        <p className="text-sm text-gray-400">未获取到在用设备</p>
      )}

      {revokeError && (
        <div className="flex items-center gap-2 p-3 bg-red-50 border border-red-100 rounded-lg text-sm text-red-600">
          <i className="ri-error-warning-line" />
          {revokeError}
        </div>
      )}

      {hint && (
        <div className="flex items-center gap-2 p-3 bg-teal-50 border border-teal-100 rounded-lg text-sm text-teal-700">
          <i className="ri-checkbox-circle-line" />
          {hint}
        </div>
      )}

      {!loadError &&
        sessions?.map((s) => (
          <div
            key={s.id}
            className="rounded-lg border border-gray-100 bg-white p-3.5 space-y-2"
          >
            <div className="flex items-center gap-2">
              <i
                className={`${s.current ? 'ri-smartphone-line' : 'ri-computer-line'} text-gray-500`}
              />
              <p className="text-sm font-medium text-gray-900 truncate">{s.deviceLabel}</p>
              {s.current && (
                <span className="ml-auto shrink-0 px-2 py-0.5 bg-teal-50 text-teal-600 text-xs font-medium rounded-full">
                  本机
                </span>
              )}
            </div>
            <div className="space-y-1 text-xs text-gray-500">
              <p>登录时间：{formatInstant(s.loginAt)}</p>
              <p>IP 地址：{s.ip ?? '-'}</p>
              <p>有效期至：{toLocalDateString(s.expiresAt) ?? '-'}</p>
            </div>

            {confirmId === s.id ? (
              <div className="flex items-center justify-between gap-3 pt-1 border-t border-gray-100">
                <p className="text-xs text-gray-600">
                  {s.current
                    ? '下线后本机需要重新登录才能继续使用。'
                    : `${s.deviceLabel} 将立即退出登录，需要重新输入账号密码。`}
                </p>
                <div className="flex gap-2 shrink-0">
                  <button
                    type="button"
                    onClick={() => setConfirmId(null)}
                    className="px-3 py-1.5 text-xs text-gray-600 border border-gray-200 rounded-lg hover:bg-gray-50 cursor-pointer whitespace-nowrap"
                  >
                    取消
                  </button>
                  <button
                    type="button"
                    onClick={() => handleRevoke(s)}
                    className="px-3 py-1.5 text-xs font-medium text-white bg-red-500 hover:bg-red-600 rounded-lg cursor-pointer whitespace-nowrap"
                  >
                    确认下线
                  </button>
                </div>
              </div>
            ) : (
              <div className="flex justify-end pt-1">
                <button
                  type="button"
                  onClick={() => setConfirmId(s.id)}
                  disabled={revokingId !== null}
                  className={`px-3 py-1.5 text-xs font-medium rounded-lg border transition-colors cursor-pointer whitespace-nowrap disabled:opacity-50 ${
                    s.current
                      ? 'text-gray-600 border-gray-200 hover:bg-gray-50'
                      : 'text-red-600 border-red-200 hover:bg-red-50'
                  }`}
                >
                  {revokingId === s.id ? '下线中...' : s.current ? '退出本机登录' : '下线该设备'}
                </button>
              </div>
            )}
          </div>
        ))}
    </div>
  );
}
